/**
 * One party, on one page: what they have bought, what they have paid, what is
 * still open, and how that moved month by month.
 *
 * Built entirely from the account statement the profile has already fetched —
 * no second round trip and, more importantly, no second definition of the
 * money. Every figure here is the same `total - amount_paid` the Account tab,
 * the payments list and the printed receipt use, so the report can never
 * disagree with the pages it summarises.
 *
 * Everything is grouped PER CURRENCY and never summed across them: a customer
 * who owes 400 USD and 600,000 IQD does not owe 600,400 of anything.
 */
import type { PortalStatement } from '@erp/shared';
import { Coins, FileText, HandCoins, Layers, TrendingUp, Wallet } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import { EmptyState, StatCard } from '../../components/ui/index.js';
import { formatDate, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

/** How many months of history the trend shows. */
const TREND_MONTHS = 12;
/** Pixel height of a bar track. Explicit, so the bars' percentages resolve. */
const BAR_TRACK = 150;

interface MonthBucket {
  key: string;
  invoiced: number;
  collected: number;
}

/** 'YYYY-MM' for a date string, without dragging a date library in. */
function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

export function PartyReportTab({ statement, loading, side = 'customer' }: {
  statement: PortalStatement | null;
  loading: boolean;
  side?: 'customer' | 'vendor';
}): JSX.Element {
  const { t, i18n } = useTranslation('parties');
  const locale = i18n.language;
  const isVendor = side === 'vendor';
  const money = (a: string | number, c: string): string =>
    formatMoney(typeof a === 'number' ? a.toFixed(4) : a, { locale, currency: c });

  const report = useMemo(() => {
    if (!statement) return null;
    const invoices = statement.invoices;
    const payments = statement.payments;

    // The currency this party mostly trades in — the one the trend is drawn
    // for, because a chart mixing currencies would be a chart of nothing.
    const primary = [...statement.summary]
      .sort((a, b) => Number(b.invoiced) - Number(a.invoiced))[0]?.currencyCode
      ?? invoices[0]?.currencyCode
      ?? payments[0]?.currencyCode
      ?? null;

    const inPrimary = primary ? invoices.filter((i) => i.currencyCode === primary) : [];
    const totals = inPrimary.map((i) => Number(i.total) || 0);
    const largest = totals.length ? Math.max(...totals) : 0;
    const average = totals.length ? totals.reduce((s, n) => s + n, 0) / totals.length : 0;

    // First and last time this party did anything at all — the two dates that
    // tell you whether a balance is fresh or has been sitting there for a year.
    const dates = [
      ...invoices.map((i) => i.docDate),
      ...payments.map((p) => p.paymentDate),
    ].filter(Boolean).sort();

    // Last 12 months, including the empty ones: gaps in trade are the point of
    // a trend, and skipping them would draw a busier customer than there is.
    const buckets: MonthBucket[] = [];
    const now = new Date();
    for (let i = TREND_MONTHS - 1; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        invoiced: 0,
        collected: 0,
      });
    }
    const byKey = new Map(buckets.map((b) => [b.key, b]));
    for (const inv of invoices) {
      if (primary && inv.currencyCode !== primary) continue;
      const b = byKey.get(monthKey(inv.docDate));
      if (b) b.invoiced += Number(inv.total) || 0;
    }
    for (const p of payments) {
      if (primary && p.currencyCode !== primary) continue;
      const b = byKey.get(monthKey(p.paymentDate));
      if (b) b.collected += Number(p.amount) || 0;
    }
    const peak = Math.max(1, ...buckets.map((b) => Math.max(b.invoiced, b.collected)));

    return {
      primary,
      largest,
      average,
      invoiceCount: invoices.length,
      paymentCount: payments.length,
      openCount: invoices.filter((i) => Number(i.balance) > 0.0001).length,
      first: dates[0] ?? null,
      last: dates[dates.length - 1] ?? null,
      buckets,
      peak,
    };
  }, [statement]);

  if (loading && !statement) {
    return <Card className="p-4"><Skeleton className="h-64 w-full" /></Card>;
  }
  if (!statement || !report || (report.invoiceCount === 0 && report.paymentCount === 0)) {
    return (
      <Card className="p-6">
        <EmptyState
          title={t('report.empty', { defaultValue: 'Nothing to report yet.' })}
          description={t('report.emptyHint', { defaultValue: 'Once this party has documents or payments, their totals and trend appear here.' })}
        />
      </Card>
    );
  }

  const monthLabel = (key: string): string => {
    const [y, m] = key.split('-');
    return new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(Number(y), Number(m) - 1, 1));
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Money, per currency. Three tiles each so the pair always reads
          "this much went out, this much came back, this much is still open". */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {statement.summary.map((s) => {
          const open = Number(s.balance) > 0.0001;
          return [
            <StatCard
              key={`inv-${s.currencyCode}`}
              label={`${isVendor ? t('account.billed', { defaultValue: 'Billed' }) : t('account.invoiced', { defaultValue: 'Invoiced' })} · ${s.currencyCode}`}
              value={money(s.invoiced, s.currencyCode)}
              icon={FileText}
              tone="info"
            />,
            <StatCard
              key={`paid-${s.currencyCode}`}
              label={`${t('account.paid', { defaultValue: 'Paid' })} · ${s.currencyCode}`}
              value={money(s.paid, s.currencyCode)}
              icon={HandCoins}
              tone="success"
            />,
            <StatCard
              key={`bal-${s.currencyCode}`}
              label={`${isVendor ? t('account.weOwe', { defaultValue: 'We owe' }) : t('account.owes', { defaultValue: 'Outstanding' })} · ${s.currencyCode}`}
              value={money(s.balance, s.currencyCode)}
              icon={Wallet}
              tone={open ? 'warning' : 'success'}
            />,
          ];
        })}
      </div>

      {/* Shape of the trading relationship, rather than its size. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard
          label={isVendor ? t('report.bills', { defaultValue: 'Bills' }) : t('report.invoices', { defaultValue: 'Invoices' })}
          value={formatNumber(report.invoiceCount, { locale })}
          icon={Layers}
          tone="brand"
          hint={t('report.openCount', { defaultValue: '{{n}} still open', n: report.openCount })}
        />
        <StatCard
          label={t('tabs.payments', { defaultValue: 'Payments' })}
          value={formatNumber(report.paymentCount, { locale })}
          icon={HandCoins}
        />
        {report.primary && (
          <StatCard
            label={t('report.average', { defaultValue: 'Average' })}
            value={money(report.average, report.primary)}
            icon={Coins}
            hint={report.primary}
          />
        )}
        {report.primary && (
          <StatCard
            label={t('report.largest', { defaultValue: 'Largest' })}
            value={money(report.largest, report.primary)}
            icon={TrendingUp}
            hint={report.primary}
          />
        )}
        <StatCard
          label={t('report.activeSince', { defaultValue: 'Active since' })}
          value={report.first ? formatDate(report.first, { locale }) : '—'}
          hint={report.last
            ? t('report.lastActivity', { defaultValue: 'last {{date}}', date: formatDate(report.last, { locale }) })
            : undefined}
        />
      </div>

      {/* Twelve months of trade, in the currency they mostly use. Deliberately
          plain bars: the question is "is this customer growing or going quiet",
          which a shape answers and a table does not. */}
      {report.primary && (
        <Card className="p-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">
              {t('report.trend', { defaultValue: 'Last 12 months' })}
              <span className="ms-2 font-mono text-xs font-normal text-fg-muted">{report.primary}</span>
            </h3>
            <div className="flex items-center gap-4 text-xs text-fg-muted">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-brand" aria-hidden />
                {isVendor ? t('account.billed', { defaultValue: 'Billed' }) : t('account.invoiced', { defaultValue: 'Invoiced' })}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-success" aria-hidden />
                {t('account.paid', { defaultValue: 'Paid' })}
              </span>
            </div>
          </div>
          {/* The track carries an EXPLICIT height. A percentage height only
              resolves against a parent that has a definite one, and the column
              below sits in an `items-end` row — which makes it content-height,
              so `h-full` here would have been auto and every bar would have
              collapsed to nothing. */}
          <div className="flex gap-1.5 sm:gap-2">
            {report.buckets.map((b) => (
              <div key={b.key} className="flex min-w-0 flex-1 flex-col">
                <div className="flex items-end justify-center gap-[2px]" style={{ height: BAR_TRACK }}>
                  {([
                    ['invoiced', b.invoiced, 'bg-brand/80'],
                    ['collected', b.collected, 'bg-success/80'],
                  ] as const).map(([kind, value, tone]) => (
                    <div
                      key={kind}
                      className={`w-1/2 rounded-t-sm ${tone}`}
                      style={{
                        height: `${(value / report.peak) * 100}%`,
                        // A small month must still be visible; an empty one
                        // must still read as empty.
                        minHeight: value > 0 ? 2 : 0,
                      }}
                      title={`${monthLabel(b.key)} · ${money(value, report.primary!)}`}
                    />
                  ))}
                </div>
                <span className="mt-1 truncate text-center text-[0.65rem] text-fg-muted">{monthLabel(b.key)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* How old the debt is. Only worth the space when there IS debt. */}
      {statement.aging.length > 0 && statement.summary.some((s) => Number(s.balance) > 0.0001) && (
        <Card className="overflow-x-auto p-0">
          <h3 className="border-b border-border px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-fg-muted">
            {t('report.aging', { defaultValue: 'How old the balance is' })}
          </h3>
          <table className="w-full text-sm">
            <thead className="bg-bg-muted text-xs uppercase tracking-wide text-fg-subtle">
              <tr>
                <th className="px-3 py-2 text-start">{t('report.currency', { defaultValue: 'Currency' })}</th>
                <th className="px-3 py-2 text-end">{t('account.aging.current', { defaultValue: 'Current' })}</th>
                <th className="px-3 py-2 text-end">1–30</th>
                <th className="px-3 py-2 text-end">31–60</th>
                <th className="px-3 py-2 text-end">61–90</th>
                <th className="px-3 py-2 text-end">90+</th>
                <th className="px-3 py-2 text-end font-semibold">{t('account.aging.total', { defaultValue: 'Total' })}</th>
              </tr>
            </thead>
            <tbody>
              {statement.aging.map((a) => (
                <tr key={a.currencyCode} className="border-t border-border font-mono">
                  <td className="px-3 py-2 text-start">{a.currencyCode}</td>
                  <td className="px-3 py-2 text-end">{money(a.current, a.currencyCode)}</td>
                  <td className="px-3 py-2 text-end">{money(a.days_1_30, a.currencyCode)}</td>
                  <td className="px-3 py-2 text-end">{money(a.days_31_60, a.currencyCode)}</td>
                  <td className="px-3 py-2 text-end">{money(a.days_61_90, a.currencyCode)}</td>
                  <td className="px-3 py-2 text-end text-danger">{money(a.days_90_plus, a.currencyCode)}</td>
                  <td className="px-3 py-2 text-end font-semibold">{money(a.total, a.currencyCode)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
